import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/utils/invokeEdgeFunction";
import { useToast } from "@/hooks/use-toast";
import { useServerTime } from "@/hooks/useServerTime";
import { CANDIDATE_STATUS, EVALUATION_ROUNDS } from "@/types/enums";
import { useQueryClient } from "@tanstack/react-query";
import type {
  Candidate,
  Question,
  CandidateResponse,
  College,
  QuestionGroup,
} from "@/types/database";

interface UseAssessmentSessionProps {
  candidate: Candidate | null;
  college: College | null;
  questions: Question[];
  questionGroups: QuestionGroup[];
  existingResponses: CandidateResponse[];
  clientIP: string; // Add clientIP as a prop instead of fetching it
  onSubmitComplete?: () => void;
  onGroupComplete?: () => Promise<void> | void;
  onAutoSubmit?: () => void; // Changed to void, saving logic will be in PublicAssessment
}

export function useAssessmentSession({
  candidate,
  college,
  questions,
  questionGroups,
  existingResponses,
  clientIP, // Use clientIP from props
  onSubmitComplete,
  onGroupComplete,
  onAutoSubmit, // Destructure new prop
}: UseAssessmentSessionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { offset: serverTimeOffset } = useServerTime();
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGroupTransition, setShowGroupTransition] = useState(false);
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);
  const submissionRef = useRef(false);
  // Sticky flag: once the assessment is committed, never invoke submit again.
  const submittedRef = useRef(false);

  // Check if assessment is not started (new state)
  const isAssessmentNotStarted = !candidate?.assesment_started_at;

  // Start assessment function to be called from UI
  const startAssessment = async (): Promise<{
    success: boolean;
    error?: any;
  }> => {
    if (!candidate || !college?.duration_minutes || !clientIP) {
      return { success: false, error: { message: "Missing required data" } };
    }

    try {
      await invokeEdgeFunction("start-assessment", {
        body: {
          candidateId: candidate.id,
          clientIP,
        },
      });

      // Invalidate candidate query to refetch updated status
      await queryClient.invalidateQueries({
        queryKey: ["candidate-by-token"],
      });

      toast({
        title: "Assessment Started",
        description: "Your assessment has been started successfully.",
      });

      return { success: true };
    } catch (error) {
      console.error("Error starting assessment:", error);
      return { success: false, error };
    }
  };

  // Calculate dynamic timer based on start time and duration
  useEffect(() => {
    if (
      !candidate?.assesment_started_at ||
      serverTimeOffset === null
    )
      return;

    // If assessment is already submitted, stop the timer and prevent auto-submission
    if (candidate.assesment_submitted_at) {
      setTimeRemaining(0); // Ensure time remaining is 0
      setHasAutoSubmitted(false); // Reset auto-submit flag
      return;
    }

    // Reset auto-submit flag when timer restarts
    setHasAutoSubmitted(false);

    const calculateTimeRemaining = (offset: number) => {
      const now = Date.now() + offset; // Use local time with server offset

      const fallbackEndTime = (() => {
        if (!college?.duration_minutes) {
          return null;
        }

        return (
          new Date(candidate.assesment_started_at!).getTime() +
          college.duration_minutes * 60 * 1000
        );
      })();

      const endTime = candidate.assessment_ends_at
        ? new Date(candidate.assessment_ends_at).getTime()
        : fallbackEndTime;

      if (endTime === null) {
        return null;
      }

      const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
      return remaining;
    };

    // Set initial time
    const initialTime = calculateTimeRemaining(serverTimeOffset);

    if (initialTime === null) {
      return;
    }

    setTimeRemaining(initialTime);

    // If time has already expired, trigger auto-submit once
    if (initialTime <= 0) {
      if (
        !candidate.assesment_submitted_at &&
        !hasAutoSubmitted &&
        !isSubmitting
      ) {
        setHasAutoSubmitted(true);
        onAutoSubmit?.();
      }
      return;
    }

    // Update timer every second
    const timer = setInterval(() => {
      const remaining = calculateTimeRemaining(serverTimeOffset);

      if (remaining === null) {
        clearInterval(timer);
        return;
      }

      setTimeRemaining(remaining);

      if (remaining <= 0) {
        clearInterval(timer);
        if (
          !candidate.assesment_submitted_at &&
          !hasAutoSubmitted &&
          !isSubmitting
        ) {
          setHasAutoSubmitted(true);
          onAutoSubmit?.();
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [
    candidate?.assessment_ends_at,
    candidate?.assesment_started_at,
    candidate?.assesment_submitted_at,
    college?.duration_minutes,
    serverTimeOffset,
    onAutoSubmit,
    hasAutoSubmitted,
    isSubmitting,
  ]);

  const handleNextGroup = async () => {
    if (!candidate || !questionGroups) return;

    const currentGroupIndex = candidate.current_group_index || 0;
    const nextGroupIndex = currentGroupIndex + 1;

    if (nextGroupIndex < questionGroups.length) {
      // Show transition dialog
      setShowGroupTransition(true);
    } else {
      // All groups completed, submit assessment
      await handleSubmitAssessment();
    }
  };

  const handlePreviousGroup = async () => {
    if (!candidate || !questionGroups) return;

    const currentGroupIndex = candidate.current_group_index || 0;
    const previousGroupIndex = currentGroupIndex - 1;

    if (previousGroupIndex >= 0) {
      try {
        const { error } = await supabase
          .from("candidates")
          .update({ current_group_index: previousGroupIndex })
          .eq("id", candidate.id);

        if (error) throw error;

        await onGroupComplete?.();
      } catch (error) {
        console.error("Error moving to previous group:", error);
        toast({
          title: "Error",
          description: "Failed to move to previous question group.",
          variant: "destructive",
        });
      }
    }
  };

  const handleContinueToNextGroup = async () => {
    if (!candidate || !questionGroups) return;

    const currentGroupIndex = candidate.current_group_index || 0;
    const nextGroupIndex = currentGroupIndex + 1;

    try {
      const { error } = await supabase
        .from("candidates")
        .update({ current_group_index: nextGroupIndex })
        .eq("id", candidate.id);

      if (error) throw error;

      await onGroupComplete?.();
      setShowGroupTransition(false);
    } catch (error) {
      console.error("Error moving to next group:", error);
      toast({
        title: "Error",
        description: "Failed to move to next question group.",
        variant: "destructive",
      });
    }
  };

  const handleSubmitAssessment = async (isAutoSubmit = false) => {
    if (!candidate) return;

    // Once submitted in this session, never invoke the edge function again.
    if (submittedRef.current) {
      return;
    }

    if (submissionRef.current) {
      return;
    }

    // Prevent multiple submissions if already submitted
    if (candidate.assesment_submitted_at) {
      submittedRef.current = true;
      // Only show "Already Submitted" toast if it's a manual submission attempt
      // and not an auto-submit that just completed.
      if (!isAutoSubmit) {
        toast({
          title: "Already Submitted",
          description: "Assessment has already been submitted.",
          variant: "destructive",
        });
      }
      return;
    }

    submissionRef.current = true;
    setIsSubmitting(true);
    try {
      await invokeEdgeFunction("submit-assessment", {
        body: {
          candidateId: candidate.id,
          isAutoSubmit,
        },
      });

      // Invalidate candidate query to refetch updated status
      await queryClient.invalidateQueries({ queryKey: ["candidate-by-token"] });

      onSubmitComplete?.();
      submittedRef.current = true;

      if (isAutoSubmit) {
        toast({
          title: "Time's Up!",
          description:
            "Assessment has been automatically submitted as time expired.",
        });
      }
    } catch (error: any) {
      console.error("Error submitting assessment:", error);
      // Check if assessment was already submitted (edge fn returns this code in response body)
      const errorCode = error?.responseBody?.errorCode ?? error?.errorCode;
      if (errorCode === "ALREADY_SUBMITTED") {
        // The assessment is already committed; treat as success and stop retrying.
        submittedRef.current = true;
        if (!isAutoSubmit) {
          toast({
            title: "Already Submitted",
            description: "Assessment has already been submitted.",
            variant: "destructive",
          });
        }
        return;
      }
      if (!isAutoSubmit) {
        toast({
          title: "Error",
          description: "Failed to submit assessment. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      submissionRef.current = false;
      setIsSubmitting(false);
    }
  };

  const currentGroupIndex = candidate?.current_group_index || 0;
  const isLastGroup = currentGroupIndex === (questionGroups?.length || 1) - 1;
  const currentGroup = questionGroups?.[currentGroupIndex] || null;
  const nextGroup = questionGroups?.[currentGroupIndex + 1] || null;
  const canGoPreviousGroup = currentGroupIndex > 0;
  const canGoNextGroup = currentGroupIndex < (questionGroups?.length || 1) - 1;

  return {
    timeRemaining,
    showGroupTransition,
    currentGroup,
    nextGroup,
    handleNextGroup,
    handlePreviousGroup,
    handleContinueToNextGroup,
    handleSubmitAssessment,
    setShowGroupTransition,
    canGoPreviousGroup,
    canGoNextGroup,
    isSubmitting,
    isLastGroup,
    currentGroupIndex,
    totalGroups: questionGroups?.length || 0,
    isAssessmentNotStarted,
    startAssessment,
  };
}
